import {useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {fetchIngredients, setIngredients} from "../slices/dishAdditionsSlice";
import {getAllergens, getBanners, getLabels, setAllergens, setBanners, setLabels} from "../slices/dishFormSlice";
import {useGetTranslation} from "./useGetTranslation";

export const usePopulateMenuItemFormCollections = () => {
    const dispatch = useDispatch();
    const getTranslation = useGetTranslation();
    const {restaurant} = useSelector(state => state.dashboard.view);
    const {banners} = useSelector(state => state.dishForm.fetchBanners);
    const {labels} = useSelector(state => state.dishForm.fetchLabels);
    const {allergens} = useSelector(state => state.dishForm.fetchAllergens);
    const restaurantLanguage = restaurant?.value.settings.language.toLowerCase();

    const populateIngredients = useCallback(
        async () => {
            const ingredientsAction = await dispatch(fetchIngredients());
            if (fetchIngredients.fulfilled.match(ingredientsAction)) {
                dispatch(setIngredients(ingredientsAction.payload?.map(ingredient => ({
                    value: ingredient,
                    label: ingredient.name[restaurantLanguage]
                })).sort((a, b) =>
                    getTranslation(a.value.name).localeCompare(getTranslation(b.value.name))
                )));
            }
        }, [dispatch, getTranslation, restaurantLanguage]);

    const populateLabels = useCallback(
        async () => {
            if (labels) return;
            const labelsAction = await dispatch(getLabels());
            if (getLabels.fulfilled.match(labelsAction)) {
                dispatch(setLabels(labelsAction.payload?.map(label => ({
                    value: label,
                    label: getTranslation(label.name)
                })).sort((a, b) =>
                    getTranslation(a.value.name).localeCompare(getTranslation(b.value.name))
                )));
            }
        }, [dispatch, getTranslation, labels]);

    const populateAllergens = useCallback(
        async () => {
            if (allergens) return;
            const allergensAction = await dispatch(getAllergens());
            if (getAllergens.fulfilled.match(allergensAction)) {
                dispatch(setAllergens(allergensAction.payload?.map(allergen => ({
                    value: allergen,
                    label: getTranslation(allergen.name)
                })).sort((a, b) =>
                    getTranslation(a.value.name).localeCompare(getTranslation(b.value.name))
                )));
            }
        }, [allergens, dispatch, getTranslation])

    const populateBanners = useCallback(
        async () => {
            if (banners) return;
            const bannersAction = await dispatch(getBanners());
            if (getBanners.fulfilled.match(bannersAction)) {
                dispatch(setBanners(bannersAction.payload?.map(banner => ({
                    value: banner,
                    label: getTranslation(banner.name)
                })).sort((a, b) =>
                    getTranslation(a.value.name).localeCompare(getTranslation(b.value.name))
                )));
            }
        }, [banners, dispatch, getTranslation]);

    return async () => {
        await populateIngredients();
        await populateLabels();
        await populateAllergens();
        await populateBanners();
    }
}